import { useState, useRef, useMemo, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, EntityModelVersionsResponse, EntityModelJson } from '../lib/api'
import { toFlatEntityModel } from '../lib/entityModel'
import {
  UploadCloudIcon, FileJsonIcon, CheckCircleIcon, AlertCircleIcon,
  ClockIcon, BuildingIcon,
} from 'lucide-react'
import EntityModelColumns from './EntityModelColumns'

export interface EntityModelReplaceState {
  // Whether a model is shown (so a Replace makes sense vs. a first upload).
  present: boolean
  uploading: boolean
  // Stable opener for the hidden file picker, hosted by this component.
  open: () => void
}

interface EntityModelProps {
  tenantId: string
  // Reports whether AI chat should be offered: a model is displayed AND the active
  // (not previous) version is selected. Lets the parent show/hide chat affordances.
  onChatAvailableChange?: (available: boolean) => void
  // Reports Replace state + a stable opener so the parent can host the Replace
  // button (e.g. in the tab row) instead of rendering it inside this component.
  onReplaceStateChange?: (state: EntityModelReplaceState) => void
}

export default function EntityModel({ tenantId, onChatAvailableChange, onReplaceStateChange }: EntityModelProps) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  // Raw parsed blob held for preview before/while the server save runs.
  const [previewModel, setPreviewModel] = useState<Record<string, any> | null>(null)
  // Which saved version the card + columns show. Active (current) is the default.
  const [viewMode, setViewMode] = useState<'current' | 'previous'>('current')

  const { data: record, isLoading } = useQuery<EntityModelVersionsResponse>({
    queryKey: ['entityModel', tenantId],
    queryFn: () => apiClient.getEntityModel(tenantId),
    enabled: !!tenantId,
  })

  // Newest version is current, second-newest is previous (backend keeps 2).
  const sortedVersions = useMemo(
    () => [...(record?.versions ?? [])].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    ),
    [record],
  )
  const currentVersion = sortedVersions[0]
  const previousVersion = sortedVersions[1]

  const currentFlat = useMemo(
    () => (currentVersion ? toFlatEntityModel(currentVersion.model_data) : null),
    [currentVersion],
  )
  const previousFlat = useMemo(
    () => (previousVersion ? toFlatEntityModel(previousVersion.model_data) : null),
    [previousVersion],
  )

  const uploadMutation = useMutation({
    mutationFn: (modelData: Record<string, any>) => apiClient.uploadEntityModel(tenantId, modelData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entityModel', tenantId] })
      // Clear the local preview so display reverts to the now-current server version.
      setPreviewModel(null)
      setViewMode('current')
      showToast('Entity model saved to server', 'success')
    },
    onError: (err) => {
      showToast(err instanceof Error ? err.message : 'Failed to upload entity model', 'error')
    },
  })

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  const handleFile = (file: File) => {
    if (!file.name.endsWith('.json')) {
      showToast('Please upload a .json file', 'error')
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => {
      let parsed: Record<string, any>
      try {
        parsed = JSON.parse(e.target?.result as string)
      } catch {
        showToast('Invalid JSON file — could not parse', 'error')
        return
      }
      // Guard against uploading another tenant's model.
      if (typeof parsed.tenant_id === 'string' && parsed.tenant_id !== tenantId) {
        showToast('This entity model belongs to a different tenant', 'error')
        return
      }
      setPreviewModel(parsed)
      uploadMutation.mutate(parsed)
    }
    reader.readAsText(file)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })

  const uploading = uploadMutation.isPending

  // Fall back to current whenever there's no previous version to show.
  const effectiveView = viewMode === 'previous' && previousFlat ? 'previous' : 'current'
  const selectedVersion = effectiveView === 'previous' ? previousVersion : currentVersion
  const selectedFlat = effectiveView === 'previous' ? previousFlat : currentFlat

  // Prefer in-memory preview; otherwise show the selected (active/previous) version.
  const displayModel: EntityModelJson | null = previewModel ? toFlatEntityModel(previewModel) : selectedFlat

  // Chat is grounded in the active model, so only offer it when a model is shown
  // AND the active (not previous) version is selected.
  const chatAvailable = !!displayModel && effectiveView !== 'previous'
  useEffect(() => {
    onChatAvailableChange?.(chatAvailable)
  }, [chatAvailable, onChatAvailableChange])

  // Expose Replace state + a stable opener so the parent can host the button.
  const openReplacePicker = useCallback(() => fileInputRef.current?.click(), [])
  const hasModel = !!displayModel
  useEffect(() => {
    onReplaceStateChange?.({ present: hasModel, uploading, open: openReplacePicker })
  }, [hasModel, uploading, openReplacePicker, onReplaceStateChange])

  return (
    <div className="space-y-5">
      {/* ── Toast ── */}
      {toast && (
        <div
          className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-medium border ${toast.type === 'success'
            ? 'bg-green-50 border-green-100 text-green-700'
            : 'bg-red-50 border-red-100 text-red-600'
            }`}
        >
          {toast.type === 'success'
            ? <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
            : <AlertCircleIcon className="h-4 w-4 flex-shrink-0" />
          }
          {toast.message}
        </div>
      )}
      {/* Hidden file input — shared by the upload zone and the Replace button */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleInputChange}
      />
      {/* ── Server status card (always shown) ── */}
      {(
        isLoading ? (
          <div className="bg-white border border-gray-100 rounded-xl px-6 py-8 text-center text-sm text-gray-400">
            Loading entity model…
          </div>
        ) : selectedVersion && selectedFlat ? (
          <div className="bg-white border border-gray-100 p-2 mb-5 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-700">
                {effectiveView === 'previous' ? 'Previous Entity Model' : 'Current Entity Model'}
              </h3>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode('current')}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border transition-colors ${effectiveView === 'current'
                    ? 'bg-green-50 text-green-700 border-green-100'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                    }`}
                >
                  <CheckCircleIcon className="h-3 w-3" />
                  Active
                </button>
                {previousFlat && (
                  <button
                    type="button"
                    onClick={() => setViewMode('previous')}
                    className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full border transition-colors ${effectiveView === 'previous'
                      ? 'bg-gray-700 text-white border-gray-700'
                      : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                      }`}
                  >
                    Previous
                  </button>
                )}
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              <div className="flex items-center px-5 py-3.5 gap-3">
                <BuildingIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-500 w-36">Company</span>
                <span className="text-sm font-semibold text-gray-900 capitalize">{selectedFlat.company}</span>
              </div>
              <div className="flex items-center px-5 py-3.5 gap-3">
                <FileJsonIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-500 w-36">Total Entities</span>
                <span className="text-sm font-semibold text-gray-900">{selectedFlat.total_entities}</span>
              </div>
              <div className="flex items-center px-5 py-3.5 gap-3">
                <ClockIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-500 w-36">Last Updated</span>
                <span className="text-sm text-gray-700">{formatDate(selectedVersion.created_at)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-xl px-6 py-8 text-center">
            <p className="text-sm text-gray-400">No entity model uploaded yet</p>
          </div>
        )
      )}
      {/* ── Upload Zone (only when no model is loaded yet) ── */}
      {!displayModel && (
        <div
          className={`relative bg-white border-2 border-dashed rounded-xl transition-colors ${dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${dragOver ? 'bg-primary-100' : 'bg-gray-100'
              }`}>
              {uploading
                ? <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                : <UploadCloudIcon className={`h-5 w-5 ${dragOver ? 'text-primary-500' : 'text-gray-400'}`} />
              }
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-1">
              {uploading ? 'Uploading…' : 'Upload entity model'}
            </p>
            <p className="text-xs text-gray-400 mb-4">Drag & drop a JSON file, or click to browse</p>
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FileJsonIcon className="h-4 w-4" />
              Choose .json file
            </button>
          </div>
        </div>
      )}
      {/* ── Column visualization ── */}
      {displayModel && <EntityModelColumns model={displayModel} />}
    </div>
  )
}
